import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@dc-shared-web/lib'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { Icon } from '@dc-shared-web/components/base'
import { faXmark } from '@document-crunch/pro-regular-svg-icons'

const Drawer = DialogPrimitive.Root

const DrawerTrigger = DialogPrimitive.Trigger

const DrawerPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
)
DrawerPortal.displayName = 'DrawerPortal'

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'tw-fixed tw-inset-0 tw-z-50 tw-bg-neutral-600/60 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
      className
    )}
    {...props}
  />
))
DrawerOverlay.displayName = 'DrawerOverlay'

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseBtn?: boolean
  }
>(({ className, children, showCloseBtn = true, ...props }, ref) => (
  <DrawerPortal>
    <div className='dc-shared-web'>
      <DrawerOverlay>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-50 tw-flex tw-flex-col tw-w-full tw-bg-background tw-p-4 tw-shadow-lg',
            'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out',
            'data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
            'data-[state=closed]:tw-slide-out-to-bottom data-[state=open]:tw-slide-in-from-bottom',
            'tw-rounded-t-lg',
            className
          )}
          {...props}
        >
          {showCloseBtn && (
            <DialogPrimitive.Close
              asChild
              className='tw-absolute tw-right-3 tw-top-3'
            >
              <Button
                variant='ghost'
                size='icon'
                className='tw-h-6 tw-w-6 [&_svg]:tw-text-black'
                aria-label='Close'
              >
                <Icon icon={faXmark} className='tw-h-4 tw-w-4' />
              </Button>
            </DialogPrimitive.Close>
          )}
          {children}
        </DialogPrimitive.Content>
      </DrawerOverlay>
    </div>
  </DrawerPortal>
))
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('tw-flex tw-flex-col tw-space-y-1.5', className)}
    {...props}
  />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('tw-flex tw-justify-end tw-space-x-2', className)}
    {...props}
  />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('tw-text-xl tw-font-normal tw-leading-none', className)}
    {...props}
  />
))
DrawerTitle.displayName = 'DrawerTitle'

export {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
}
